import { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import Swal from 'sweetalert2';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import PaginationBar from '../components/ui/PaginationBar';
import { usePagination } from '../hooks/usePagination';
import { listPayments, getPaymentStats, deletePayment, PaymentItem } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import TabFilterBar from '../components/ui/TabFilterBar';
import EmptyState from '../components/ui/EmptyState';
import PaymentFormDialog from '../components/payments/PaymentFormDialog';
import PaymentCards from '../components/payments/PaymentCards';
import ViewToggle, { useViewMode } from '../components/ui/ViewToggle';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'pending', label: 'Pending' },
];

const fmtMoney = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PaymentsPage = () => {
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalReceived: 0, totalPaid: 0, balance: 0, receivedDeltaPct: null as number | null, paidDeltaPct: null as number | null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentItem | null>(null);
  const bulk = useBulkSelect();
  const [view, setView] = useViewMode('quillstack.payments.view', 'grid');
  const pager = usePagination(items);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === 'pending' ? { status: 'pending' } : tab === 'all' ? {} : { type: tab };
      const [list, s] = await Promise.all([listPayments(params), getPaymentStats()]);
      setItems(list);
      setStats(s);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (p: PaymentItem) => {
    const result = await Swal.fire({ title: 'Delete payment?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    await deletePayment(p._id);
    load();
  };

  return (
    <Box>
      <PageHeader
        title="Payments"
        actions={
          <Button startIcon={<AddRounded />} variant="contained" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add Payment
          </Button>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5, mb: 3 }}>
        <StatCard icon={PaymentsRounded} label="Total Received" value={fmtMoney(stats.totalReceived)} deltaPct={stats.receivedDeltaPct ?? undefined} deltaLabel="vs last month" color="#0ca30c" />
        <StatCard icon={PaymentsRounded} label="Total Paid" value={fmtMoney(stats.totalPaid)} deltaPct={stats.paidDeltaPct ?? undefined} deltaLabel="vs last month" color="#d03b3b" />
        <StatCard icon={PaymentsRounded} label="Balance" value={fmtMoney(stats.balance)} color="#6226A7" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TabFilterBar value={tab} onChange={setTab} options={TABS} noMargin />
        <ViewToggle value={view} onChange={setView} />
      </Box>

      {!loading && items.length === 0 ? (
        <EmptyState icon={PaymentsRounded} title="No payments yet" description="Add your first income or expense entry." actionLabel="Add Payment" onAction={() => { setEditing(null); setDialogOpen(true); }} />
      ) : (
        <>
        <BulkSelectBar bulk={bulk} visibleIds={pager.pageRows.map((p) => p._id)} noun="payment" deleteOne={deletePayment} onDone={load} />
        <PaymentCards
          items={pager.pageRows}
          bulk={bulk}
          view={view}
          onEdit={(p) => { setEditing(p); setDialogOpen(true); }}
          onDelete={handleDelete}
        />
        <PaginationBar page={pager.page} totalPages={pager.totalPages} pageSize={pager.pageSize} onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize} />
        </>
      )}

      <PaymentFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} editing={editing} />
    </Box>
  );
};

export default PaymentsPage;
